import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Code2, Database, ChevronRight, GraduationCap } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';

export default function AcademyHome({ darkMode }) {
  const { t } = useTranslation();

  const courses = [
    {
      path: '/academy/sql',
      icon: <Database size={32} style={{ color: '#3b82f6' }} />,
      title: t('academy.course1Title'),
      description: t('academy.course1Desc'),
      lessons: ['SELECT', 'WHERE', 'JOINS', 'INDEXES'],
      color: '#3b82f6',
      lightBg: '#eff6ff',
      darkBg: 'rgba(37,99,235,0.1)',
    },
    {
      path: '/academy/csharp-patterns',
      icon: <Code2 size={32} style={{ color: '#8b5cf6' }} />,
      title: t('academy.course2Title'),
      description: t('academy.course2Desc'),
      lessons: ['Singleton', 'Factory', 'Repository', 'Dependency Injection'],
      color: '#8b5cf6',
      lightBg: '#f5f3ff',
      darkBg: 'rgba(139,92,246,0.1)',
    },
  ];

  const bg = darkMode ? '#111827' : '#f9fafb';
  const cardBg = darkMode ? '#1f2937' : '#ffffff';
  const textColor = darkMode ? '#f9fafb' : '#111827';
  const subtextColor = darkMode ? '#9ca3af' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const tagBg = darkMode ? '#374151' : '#f3f4f6';

  return (
    <div style={{ minHeight: '100vh', background: bg, color: textColor }}>

      {/* Hero */}
      <section style={{ padding: '5rem 1.5rem 3rem', textAlign: 'center', maxWidth: '760px', margin: '0 auto' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: darkMode ? 'rgba(37,99,235,0.15)' : '#eff6ff', color: '#3b82f6', padding: '0.4rem 1rem', borderRadius: '2rem', fontSize: '0.85rem', fontWeight: 600, marginBottom: '1.5rem' }}>
          <GraduationCap size={16} />
          {t('academy.badge')}
        </div>

        <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, lineHeight: 1.2, marginBottom: '1.25rem' }}>
          {t('academy.title')}{' '}
          <span style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {t('academy.titleHighlight')}
          </span>
          {' '}{t('academy.titleSuffix')}
        </h1>

        <p style={{ fontSize: '1.1rem', color: subtextColor, lineHeight: 1.75, maxWidth: '560px', margin: '0 auto 2rem' }}>
          {t('academy.subtitle')}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap', color: subtextColor, fontSize: '0.9rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <BookOpen size={16} style={{ color: '#3b82f6' }} />
            8 {t('academy.lessons')}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <GraduationCap size={16} style={{ color: '#8b5cf6' }} />
            2 {t('academy.courses')}
          </span>
        </div>
      </section>

      {/* Course cards */}
      <section style={{ maxWidth: '900px', margin: '0 auto', padding: '0 1.5rem 5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '1.5rem' }}>
        {courses.map((course) => (
          <Link
            key={course.path}
            to={course.path}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div
              style={{ background: cardBg, borderRadius: '1.25rem', padding: '2rem', border: `1px solid ${borderColor}`, transition: 'all 0.25s', cursor: 'pointer', height: '100%', display: 'flex', flexDirection: 'column' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 40px ${course.color}25`; e.currentTarget.style.borderColor = course.color; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = borderColor; }}
            >
              {/* Icon */}
              <div style={{ width: '64px', height: '64px', borderRadius: '1rem', background: darkMode ? course.darkBg : course.lightBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                {course.icon}
              </div>

              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.75rem' }}>{course.title}</h2>

              <p style={{ color: subtextColor, lineHeight: 1.7, marginBottom: '1.5rem', flex: 1 }}>
                {course.description}
              </p>

              {/* Lesson tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.5rem' }}>
                {course.lessons.map((lesson) => (
                  <span
                    key={lesson}
                    style={{ background: tagBg, color: subtextColor, padding: '0.25rem 0.6rem', borderRadius: '0.4rem', fontSize: '0.8rem', fontWeight: 500 }}
                  >
                    {lesson}
                  </span>
                ))}
              </div>

              {/* CTA */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: course.color, fontWeight: 600, fontSize: '0.9rem' }}>
                {t('academy.startCourse')}
                <ChevronRight size={16} />
              </div>
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}
